import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getGstBreakdown, amountInWords, buildProjectLineItems, rowTotal, isSameStateGst } from '../utils/invoiceUtils';
import logoUrl from '../assets/logos/Logo Blue Text PNG  .png';

const MARGIN = 14;
const HEADER_BLUE = [30, 94, 171];

function formatRs(amount) {
  return `Rs. ${Number(amount || 0).toFixed(2)}`;
}

export async function generateInvoicePdf({
  invoiceNumber,
  project,
  client,
  settings,
  poc = '',
  purchaseOrder = '',
  gstNo = '',
  additionalRows = [],
}) {
  const base = buildProjectLineItems(project);
  const extra = (additionalRows || [])
    .filter((row) => row.particulars?.trim())
    .map((row) => ({
      particulars: row.particulars,
      price: Number(row.price) || 0,
      qty: Number(row.qty) || 1,
      total: rowTotal(row),
    }));

  const lineItems = [...base, ...extra];
  const subtotal = lineItems.reduce((s, i) => s + i.total, 0);
  const isSameState = isSameStateGst(settings.companyState, client?.state || project.clientState);
  const gstRate = Number(settings.gstRate) || 18;
  const tax = getGstBreakdown(subtotal, gstRate, isSameState);
  const grandTotal = subtotal + tax.totalTax;

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  const loadImage = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  const logoBase64 = await loadImage(logoUrl);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - MARGIN * 2;

  // Title + logo (template layout)
  doc.setFontSize(26);
  doc.setTextColor(40, 40, 40);
  doc.setFont('helvetica', 'bold');
  doc.text('INVOICE', MARGIN, 22);

  if (logoBase64) {
    doc.addImage(logoBase64, 'PNG', pageWidth - MARGIN - 58, 10, 58, 22);
  }

  let currentY = 38;
  const leftLabelX = MARGIN;
  const leftValueX = MARGIN + 38;
  const rightLabelX = pageWidth / 2 + 4;
  const rightValueX = pageWidth / 2 + 34;

  const addFieldRow = (leftLabel, leftValue, rightLabel, rightValue) => {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(leftLabel, leftLabelX, currentY);
    if (rightLabel) doc.text(rightLabel, rightLabelX, currentY);

    doc.setFont('helvetica', 'normal');
    doc.text(String(leftValue || ''), leftValueX, currentY, { maxWidth: rightLabelX - leftValueX - 4 });
    if (rightLabel) {
      doc.text(String(rightValue || ''), rightValueX, currentY, { maxWidth: pageWidth - MARGIN - rightValueX });
    }
    currentY += 7;
  };

  addFieldRow('PROJECT NAME:', project.projectName, 'INVOICE DATE:', new Date().toLocaleDateString('en-IN'));
  addFieldRow('INVOICE TO:-', client?.name || project.client, 'INVOICE NO:', invoiceNumber);
  addFieldRow('GST NO :', gstNo, 'Product ID :', project.projectId);
  addFieldRow('POC:', poc, 'PURCHASE ORDER:', purchaseOrder);

  currentY += 4;

  // Line items table — fit page width
  const tableBody = lineItems.map((item, idx) => [
    idx + 1,
    item.particulars,
    formatRs(item.price),
    item.qty,
    formatRs(item.total),
  ]);

  const summaryRows = [
    [{ content: 'Sub Total', colSpan: 4, styles: { halign: 'right', fontStyle: 'bold' } }, { content: formatRs(subtotal), styles: { halign: 'right', fontStyle: 'bold' } }],
  ];

  if (tax.cgst) {
    summaryRows.push(
      [{ content: `SGST @ ${gstRate / 2}%`, colSpan: 4, styles: { halign: 'right' } }, { content: formatRs(tax.sgst), styles: { halign: 'right' } }],
      [{ content: `CGST @ ${gstRate / 2}%`, colSpan: 4, styles: { halign: 'right' } }, { content: formatRs(tax.cgst), styles: { halign: 'right' } }],
    );
  }
  if (tax.igst) {
    summaryRows.push(
      [{ content: `IGST @ ${gstRate}%`, colSpan: 4, styles: { halign: 'right' } }, { content: formatRs(tax.igst), styles: { halign: 'right' } }],
    );
  }

  summaryRows.push(
    [{ content: 'Total', colSpan: 4, styles: { halign: 'right', fontStyle: 'bold', fontSize: 10 } }, { content: formatRs(grandTotal), styles: { halign: 'right', fontStyle: 'bold', fontSize: 10 } }],
  );

  autoTable(doc, {
    startY: currentY,
    head: [['SL.NO.', 'PARTICULARS', 'PRICE', 'QTY', 'TOTAL']],
    body: [...tableBody, ...summaryRows],
    theme: 'grid',
    tableWidth: contentWidth,
    margin: { left: MARGIN, right: MARGIN },
    headStyles: {
      fillColor: HEADER_BLUE,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center',
      cellPadding: 2.5,
    },
    columnStyles: {
      0: { cellWidth: 14, halign: 'center' },
      1: { cellWidth: contentWidth - 14 - 28 - 14 - 28 },
      2: { cellWidth: 28, halign: 'right' },
      3: { cellWidth: 14, halign: 'center' },
      4: { cellWidth: 28, halign: 'right' },
    },
    styles: {
      fontSize: 9,
      cellPadding: 2.5,
      overflow: 'linebreak',
      valign: 'middle',
    },
    bodyStyles: {
      minCellHeight: 8,
    },
  });

  currentY = doc.lastAutoTable.finalY + 10;

  // Amount in words
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Invoice Value (in words):', MARGIN, currentY);
  doc.setFont('helvetica', 'normal');
  const words = amountInWords(grandTotal);
  doc.text(words, MARGIN + 52, currentY, { maxWidth: contentWidth - 52 });

  currentY += 14;
  const leftColX = MARGIN;
  const rightColX = pageWidth / 2 + 6;
  const lineGap = 5;

  // Payment details — left column
  doc.setFont('helvetica', 'bold');
  doc.text('Payment type: DD/Cheque', leftColX, currentY);
  doc.setFont('helvetica', 'normal');
  doc.text('DD/Cheque in the name of "THE VIDEO COMPANY"', leftColX, currentY + lineGap);
  doc.text('Payable at BTM Layout,', leftColX, currentY + lineGap * 2);
  doc.text(`GSTIN: ${settings.companyGstin || '29BJDPV6986M2ZY'}`, leftColX, currentY + lineGap * 3);

  // Online transfer — right column
  doc.setFont('helvetica', 'bold');
  doc.text('Online Transfer:', rightColX, currentY);
  doc.setFont('helvetica', 'normal');
  const bankLines = [
    'A/C Name: THE VIDEO COMPANY',
    'AC/NO: 29600200000730',
    'IFSC Code: BARB0BTMBAN',
    'Bank Name: BANK OF BARODA',
    'Bank BTM Layout, Bangalore,',
    'Address: Karnataka-560068',
  ];
  bankLines.forEach((line, i) => {
    doc.text(line, rightColX, currentY + lineGap * (i + 1));
  });

  // Footer contact info
  const footerY = pageHeight - 28;
  doc.setFontSize(8);
  doc.setTextColor(80);
  const footerText1 = 'Phone (+91- 7411611528)   |   Mail (vasant@thevideocompany.in)';
  const footerText2 = 'Location (5/2, 17th Cross, NGR Layout, Bommanahalli, Bangalore-560068)';
  doc.text(footerText1, pageWidth / 2, footerY, { align: 'center' });
  doc.text(footerText2, pageWidth / 2, footerY + 5, { align: 'center' });

  // Signature — bottom right (template: Vasant / Founder/DOP)
  doc.setTextColor(0);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  const sigX = pageWidth - MARGIN;
  const sigY = pageHeight - 18;
  doc.text('Vasant', sigX, sigY, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Founder/DOP', sigX, sigY + 5, { align: 'right' });

  doc.autoPrint();

  const pdfBytes = doc.output('arraybuffer');
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });

  return {
    blob,
    subtotal,
    cgst: tax.cgst,
    sgst: tax.sgst,
    igst: tax.igst,
    grandTotal,
    lineItems,
  };
}
